import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Container,
  Typography,
  Box,
  Button,
  useMediaQuery,
} from "@mui/material";
import SquarehubLoader from "./SquarehubLoader";
import { useTheme } from "@mui/styles";
import JobOfferList from "bundles/Candidate/components/JobOfferList";
import sh from "bundles/common/utils/sh";
import trackEvent from "bundles/common/utils/trackEvent";
import BreadcrumbsBar from "./BreadcrumbsBar";
import RelatedJobOffers from "bundles/common/components/RelatedJobOffers";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
  INDUSTRY_CATEGORIES,
} from "bundles/common/utils/categoryData.js";

const TOP_LEVEL_SECTORS = [
  { slug: "architecture" },
  { slug: "engineering" },
  { slug: "construction" },
  { slug: "administration" },
  { slug: "industry" },
];

const SECTOR_CATEGORIES = {
  architecture: ARCHITECTURE_CATEGORIES,
  engineering: ENGINEERING_CATEGORIES,
  construction: CONSTRUCTION_CATEGORIES,
  administration: ADMINISTRATION_CATEGORIES,
  industry: INDUSTRY_CATEGORIES,
};

const VISIBLE_LIMIT = 8;

const SectorJobsPage = ({ industry: industryProp }) => {
  const { industry: industryParam } = useParams();
  const industry = industryProp || industryParam;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryFromUrl = searchParams.get("q") || "";
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale) || "en";
  const intl = useIntl();

  const [jobOffers, setJobOffers] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [filterCounts, setFilterCounts] = useState({});
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAllSectors = !industry;
  const sectorLabel = industry
    ? intl.formatMessage({
      id: `company.company_industry.${industry}`,
      defaultMessage: industry,
    })
    : null;
  const categories = industry
    ? (SECTOR_CATEGORIES[industry]?.[i18nLocale] || SECTOR_CATEGORIES[industry]?.["en"] || [])
    : [];

  const catLabel = (slug) => {
    const fallback = slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return intl.formatMessage({ id: `categories.${slug}`, defaultMessage: fallback });
  };

  const sectorLabelFromSlug = (slug) =>
    intl.formatMessage({
      id: `company.company_industry.${slug}`,
      defaultMessage: slug,
    });

  const fetchCounts = () => {
    const url = industry
      ? `/${i18nLocale}/job-offers/${industry}/counts`
      : `/${i18nLocale}/job-offers/counts`;

    sh.get(url)
      .then((res) => {
        setFilterCounts(res.data || {});
        setCountsLoaded(true);
      })
      .catch(() => {
        setCountsLoaded(true);
      });
  };

  const fetchJobOffers = ({ page = 1 } = {}) => {
    const url = industry
      ? `/${i18nLocale}/job-offers/${industry}/fetch`
      : `/${i18nLocale}/job-offers/fetch`;

    setLoading(true);
    sh.get(url, {
      params: {
        authenticity_token: authenticityToken,
        page,
        ...(industry ? { industry } : {}),
        ...(queryFromUrl ? { q: queryFromUrl } : {}),
      },
    })
      .then((res) => {
        const newOffers = res.data?.data || [];
        setJobOffers((prev) => (page === 1 ? newOffers : [...prev, ...newOffers]));
        setCount(res?.data?.pagy?.count);
        setPageCount(res?.data?.pagy?.pages || 1);
        setLoading(false);
        if (page === 1) {
          trackEvent("public_jobs", "searched", {
            query: queryFromUrl,
            industry: industry || null,
            result_count: res?.data?.pagy?.count || 0,
          });
        }
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  // Reset and refetch from page 1 whenever the sector changes
  useEffect(() => {
    setJobOffers([]);
    setCount(0);
    setPage(1);
    setPageCount(1);
    setFilterCounts({});
    setCountsLoaded(false);
    setShowAllCategories(false);
    setLoading(false);
    fetchJobOffers({ page: 1 });
    fetchCounts();
  }, [industry, i18nLocale, queryFromUrl]);

  // Fetch additional pages when "See more" is clicked
  useEffect(() => {
    if (page > 1) {
      fetchJobOffers({ page });
    }
  }, [page]);

  const pageCrumbs = [
    {
      label: intl.formatMessage({ id: "menu.home" }),
      path: `/${i18nLocale}`,
    },
    {
      label: intl.formatMessage({ id: "nav.job_offers" }),
      path: `/${i18nLocale}/job-offers`,
      current: isAllSectors,
    },
    ...(industry
      ? [{ label: sectorLabel, path: `/${i18nLocale}/job-offers/${industry}`, current: true }]
      : []),
  ];

  const handleOfferClick = (offer) => {
    const offerCategorySlug = Array.isArray(offer?.categories) && offer.categories.length > 0
      ? offer.categories[0]
      : null;
    const offerIndustry = Array.isArray(offer?.company_industries) && offer.company_industries.length > 0
      ? offer.company_industries[0]
      : industry;

    const categoryLabel = offerCategorySlug ? catLabel(offerCategorySlug) : null;

    const offerCrumbs = [
      {
        label: intl.formatMessage({ id: "menu.home" }),
        path: `/${i18nLocale}`,
      },
      {
        label: intl.formatMessage({ id: "nav.job_offers" }),
        path: `/${i18nLocale}/job-offers`,
      },
      ...(offerIndustry
        ? [{
          label: offerIndustry.charAt(0).toUpperCase() + offerIndustry.slice(1),
          path: `/${i18nLocale}/job-offers/${offerIndustry}`,
        }]
        : []),
      ...(offerCategorySlug && categoryLabel
        ? [{
          label: categoryLabel,
          path: `/${i18nLocale}/job-offers/${offerIndustry}/${offerCategorySlug}`,
        }]
        : []),
    ];

    navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs: offerCrumbs } });
  };

  const filterPillSx = {
    backgroundColor: "#023331",
    borderRadius: "20px",
    px: 2,
    py: 1,
    display: "flex",
    gap: "4px",
    alignItems: "center",
    height: "33px",
  };

  const pillTextSx = () => ({
    fontFamily: "Segoe UI Semibold, sans-serif",
    color: "white",
    fontSize: "14px",
    whiteSpace: "nowrap",
  });

  return (
    <Box sx={{ backgroundColor: "#F8F8F8", minHeight: "100vh" }}>
      <Container maxWidth="lg">
        <Box pt={4}>
          <BreadcrumbsBar crumbs={pageCrumbs} />
        </Box>

        {/* Subtitle */}
        <Box mt={2} mb={3} textAlign="center">
          <Typography
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif",
              fontSize: "18px",
              color: "#111",
              lineHeight: 1.6,
              letterSpacing: "0.15px",
            }}
          >
            {intl.formatMessage({ id: "public_job_offer_index.main_index_subtitle" })}
          </Typography>
        </Box>

        {/* Filter section */}
        <Box
          sx={{
            backgroundColor: "white",
            border: "0.5px solid #8e8e8e",
            borderRadius: "16px",
            px: 1.25,
            py: 2.5,
            mb: 5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            display="flex"
            flexWrap="wrap"
            gap="4px"
            justifyContent={isAllSectors ? "center" : "flex-start"}
            alignItems="center"
            width="100%"
          >
            {isAllSectors
              ? TOP_LEVEL_SECTORS.map((sector) => (
                <a
                  href={`/${i18nLocale}/job-offers/${sector.slug}`}
                  key={sector.slug}
                  style={{ textDecoration: "none" }}
                >
                  <Box sx={filterPillSx}>
                    <Typography sx={pillTextSx(600)}>
                      {sectorLabelFromSlug(sector.slug)}
                    </Typography>
                    <Typography sx={pillTextSx()}>-</Typography>
                    <Typography sx={pillTextSx()}>
                      {countsLoaded ? (filterCounts[sector.slug] || 0) : "…"}
                    </Typography>
                  </Box>
                </a>
              ))
              : (() => {
                const activeCats = (() => {
                  if (!countsLoaded) return categories;

                  // Keep pills scoped to the current sector only.
                  // Otherwise, cross-sector slugs can generate invalid URLs and 404s.
                  return categories.filter((cat) => filterCounts[cat.slug] > 0);
                })();

                const visible = showAllCategories
                  ? activeCats
                  : activeCats.slice(0, VISIBLE_LIMIT);
                const hiddenCount = activeCats.length - VISIBLE_LIMIT;

                return (
                  <>
                    {visible.map((cat) => (
                      <a
                        href={`/${i18nLocale}/job-offers/${industry}/${cat.slug}`}
                        key={cat.slug}
                        style={{ textDecoration: "none" }}
                      >
                        <Box sx={filterPillSx}>
                          <Typography sx={pillTextSx(600)}>{catLabel(cat.slug)}</Typography>
                          <Typography sx={pillTextSx()}>-</Typography>
                          <Typography sx={pillTextSx()}>
                            {countsLoaded ? (filterCounts[cat.slug] || 0) : "…"}
                          </Typography>
                        </Box>
                      </a>
                    ))}
                    {!showAllCategories && hiddenCount > 0 && (
                      <Box
                        onClick={() => setShowAllCategories(true)}
                        sx={{
                          ...filterPillSx,
                          cursor: "pointer",
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <Typography sx={pillTextSx(600)}>+{hiddenCount}</Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
          </Box>
        </Box>

        {/* Job count — hidden until first load completes */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, visibility: loading && jobOffers.length === 0 ? "hidden" : "visible" }}>
          <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <Typography
              sx={{
                fontFamily: "Segoe UI Semibold, sans-serif",
                fontSize: "18px",
                color: "#111",
                lineHeight: 1.6,
                letterSpacing: "0.15px",
              }}
            >
              {intl.formatMessage({ id: "menu.jobs" })}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Segoe UI Semibold, sans-serif",
                fontSize: "18px",
                color: "#111",
                lineHeight: 1.6,
                letterSpacing: "0.15px",
              }}
            >
              -
            </Typography>
            <Box sx={{ backgroundColor: "#D1F812", borderRadius: "20px", px: 1, py: 0.25 }}>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "18px",
                  color: "#111",
                  lineHeight: 1.6,
                  letterSpacing: "0.15px",
                }}
              >
                {count || jobOffers.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Job list — spinner replaces list on first load, sits below on subsequent loads */}
        {loading && jobOffers.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8, mb: 4 }}>
            <SquarehubLoader size={72} centered />
          </Box>
        ) : jobOffers.length === 0 ? (
          <Box sx={{ mb: 4 }}>
            <RelatedJobOffers />
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            <JobOfferList
              offers={jobOffers}
              handleOfferClick={handleOfferClick}
              columnPerRow={isDesktop ? 2 : 1}
            />
          </Box>
        )}

        {/* Spinner while loading more, See more button when there are more pages */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          {loading && jobOffers.length > 0 ? (
            <SquarehubLoader size={48} />
          ) : page < pageCount ? (
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#166D4C",
                color: "white",
                borderRadius: "29px",
                px: 23,
                py: 1.5,
                fontSize: "14px",
                fontFamily: "Segoe UI Regular, sans-serif",
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#135a3d" },
              }}
              onClick={() => setPage((prev) => prev + 1)}
            >
              {intl.formatMessage({ id: "see_more" })}
            </Button>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
};

export default SectorJobsPage;
